/**
 * PWAInstallPrompt — non-intrusiver Install-Banner (Bottom).
 *
 * Spec: docs/design-spec.md §8 (Mobile/PWA)
 *  - Web-Push + Install-Prompt (nach 2 Sessions)
 *
 * Verhalten:
 *  - Nach 2+ Sessions UND wenn `beforeinstallprompt` verfügbar ODER iOS,
 *    erscheint ein dezenter Banner unten (über der MobileTabBar).
 *  - User kann installieren oder dauerhaft dismissen (localStorage).
 *  - Auf iOS gibt es keinen nativen Prompt — wir zeigen eine kurze Anleitung.
 *  - Nach Mount kurz verzögert eingeblendet (1500ms), damit der Banner nicht
 *    sofort beim Pageload aufpoppt.
 */
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, Share, X } from "lucide-react";
import { usePWAInstall } from "@/hooks/use-pwa-install";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { cn } from "@/lib/utils";

const SHOW_DELAY_MS = 1500;

export function PWAInstallPrompt() {
  const { shouldShowPrompt, isIOS, promptInstall, dismiss } = usePWAInstall();
  const [visible, setVisible] = useState(false);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!shouldShowPrompt) {
      setVisible(false);
      return;
    }
    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [shouldShowPrompt]);

  if (!shouldShowPrompt) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          role="dialog"
          aria-labelledby="pwa-install-title"
          aria-describedby="pwa-install-desc"
          initial={reducedMotion ? false : { y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={reducedMotion ? { opacity: 0 } : { y: 100, opacity: 0 }}
          transition={{
            duration: reducedMotion ? 0 : 0.24,
            ease: [0.16, 1, 0.3, 1],
          }}
          className={cn(
            "fixed left-3 right-3 z-50",
            // sitzt über der MobileTabBar (ca. 56px) bzw. unten am Desktop
            "bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] md:bottom-4",
            "md:left-auto md:right-4 md:max-w-sm",
            "rounded-xl border border-border-default bg-bg-surface shadow-lg",
            "p-3",
          )}
        >
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-tint text-brand-600 dark:text-brand-400"
            >
              <Download className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <h3
                id="pwa-install-title"
                className="text-sm font-semibold text-text-primary"
              >
                PayPilot installieren
              </h3>
              <p id="pwa-install-desc" className="mt-0.5 text-xs text-text-tertiary">
                {isIOS
                  ? "Tippe auf das Teilen-Icon und waehle ‚Zum Home-Bildschirm‘."
                  : "Schnellerer Zugriff, Push-Benachrichtigungen und Vollbild-Modus."}
              </p>

              <div className="mt-2.5 flex items-center gap-2">
                {isIOS ? (
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-bg-subtle px-2 py-1 text-xs text-text-secondary">
                    <Share className="h-3.5 w-3.5" aria-hidden="true" />
                    Teilen-Menue oeffnen
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      void promptInstall();
                    }}
                    className={cn(
                      "inline-flex h-8 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-xs font-medium text-white",
                      "transition-colors hover:bg-brand-700",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-surface",
                    )}
                  >
                    <Download className="h-3.5 w-3.5" aria-hidden="true" />
                    Installieren
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setVisible(false);
                    dismiss();
                  }}
                  className={cn(
                    "inline-flex h-8 items-center rounded-md px-2 text-xs font-medium text-text-secondary",
                    "transition-colors hover:bg-bg-hover hover:text-text-primary",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                  )}
                >
                  Spaeter
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setVisible(false);
                dismiss();
              }}
              aria-label="Banner schliessen"
              className={cn(
                "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                "text-text-tertiary transition-colors hover:bg-bg-hover hover:text-text-primary",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
              )}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
